import {
  createEffect,
  createSignal,
  For,
  on,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { useSearchParams } from "@solidjs/router";
import c from "class-c";
import { createMediaQuery } from "@solid-primitives/media";

import CommunityPost from "@repo/kikoff-models/community/CommunityPost";
import Illustration from "@repo/ui/branding/Illustration";
import Button from "@repo/ui/buttons/Button";
import ContainerButton from "@repo/ui/buttons/ContainerButton";
import Card from "@repo/ui/containers/Card";
import ContinuousScroll from "@repo/ui/containers/ContinuousScroll";
import Line from "@repo/ui/decoration/Line";
import FloatingWithAnchor from "@repo/ui/floating/FloatingWithAnchor";
import ListTile from "@repo/ui/info/ListTile";
import Constraints from "@repo/ui/layout/Constraints";
import Link from "@repo/ui/navigation/Link";
import UArray from "@repo/utils/UArray";

import CommunityGuidelines from "~/components/CommunityGuidelines";
import CommunityNavbar from "~/components/CommunityNavbar";
import PostContent from "~/components/post/PostContent";
import ChipContainer from "~/components/shared/ChipContainer";
import SearchInput from "~/components/shared/SearchInput";
import { useOverlaysController } from "~/overlays";
import { usePosts } from "~/state/features/posts";

import styles from "./(home).module.scss";

let scrollPosition = 0;

export default function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = usePosts();

  const [showSortMenu, setShowSortMenu] = createSignal(false);
  const sortMethod = (): CommunityPost.SortMethod =>
    UArray.includesLiteral(CommunityPost.SortMethod.list, searchParams.sort)
      ? searchParams.sort
      : "default";

  createEffect(
    on(
      [sortMethod, () => searchParams.q],
      () => setPosts.fetchPage(1, sortMethod(), searchParams.q as string),
      { defer: true }
    )
  );

  onMount(() => {
    document.body.scrollTo(0, scrollPosition);
  });

  onCleanup(() => {
    scrollPosition = document.body.scrollTop;
  });

  const isSmall = createMediaQuery("(max-width: 800px)", true);

  return (
    <Constraints class={styles.home}>
      <CommunityNavbar />
      <Header />
      <main class="pt-3">
        <div class={c`mb-6 expand ${styles["posts"]}`}>
          <div class="row-center">
            <FloatingWithAnchor
              class={styles["sort"]}
              show={showSortMenu()}
              onClickOutside={() => setShowSortMenu(false)}
              anchor={
                <ContainerButton
                  class="inline"
                  onClick={() => {
                    setShowSortMenu(!showSortMenu());
                  }}
                >
                  <ChipContainer>
                    <span class="text:regular color:moderate mr-1">
                      Sort by:
                    </span>
                    <span class="text:regular+">
                      {CommunityPost.SortMethod.format(sortMethod())}
                    </span>
                    <span class="icon:medium"></span>
                  </ChipContainer>
                </ContainerButton>
              }
            >
              <Card class={c`p-0 ${styles["menu"]}`} outline>
                <For each={CommunityPost.SortMethod.list}>
                  {(method) => (
                    <div
                      class={c`p-1.5 ${styles["item"]}`}
                      onClick={() => {
                        setShowSortMenu(false);
                        if (method !== sortMethod())
                          setSearchParams({ sort: method }, { replace: true });
                      }}
                    >
                      {CommunityPost.SortMethod.format(method)}
                    </div>
                  )}
                </For>
              </Card>
            </FloatingWithAnchor>
          </div>
          {!posts.home(sortMethod())?.length && (
            <div class="text-center text:medium color:moderate mt-10">
              {searchParams.q
                ? `Hm... we couldn't find any results for "${searchParams.q}"`
                : "No posts yet, be the first to post!"}
            </div>
          )}
          <ContinuousScroll
            next={() =>
              setPosts
                .fetchPage(
                  posts.page() + 1,
                  sortMethod(),
                  searchParams.q as string
                )
                .then((posts) => ({ done: posts.length === 0 }))
            }
          >
            <For each={posts.home(sortMethod())}>
              {(post, index) => (
                <>
                  {index() > 0 && <Line class="mt-2" />}
                  <Link
                    variant="container"
                    href={`/post/${post.id}`}
                    class="mt-2"
                  >
                    <Card class={c`p-1 ${styles["post-preview"]}`}>
                      <PostContent post={post} lineClamp={7} />
                    </Card>
                  </Link>
                </>
              )}
            </For>
          </ContinuousScroll>
        </div>

        <Show when={!isSmall()}>
          <Card dugout marginCollapsible class={styles.guidelines}>
            <CommunityGuidelines />
          </Card>
        </Show>
      </main>
    </Constraints>
  );
}

function Header() {
  const overlays = useOverlaysController();
  const isSmall = createMediaQuery("(max-width: 800px)", true);

  return (
    <div>
      <div class="row-center mt-1">
        <SearchInput placeholder="Search Kikoff Community ..." expand />
        <CreatePostButton class="ml-1" />
      </div>
      <div class="row-center">
        <div class="expand">
          <ListTile
            align="center"
            propsFor={{ title: { class: "text:heading-4" } }}
            leading={
              !isSmall() && (
                <Illustration name="credit-score-leaves-2" size="small" />
              )
            }
            title="Welcome to Kikoff Community"
          />
        </div>
      </div>
      <Show when={isSmall()}>
        <ContainerButton
          class="row-center"
          onClick={() => overlays.open("CommunityGuidelines")}
        >
          <ChipContainer class={styles["community-guidelines-chip"]}>
            <div class="text:small+">Community Guidelines</div>
            <span class="icon:medium color:primary-strong ml-1"></span>
          </ChipContainer>
        </ContainerButton>
      </Show>
      <Line class="mt-1" />
    </div>
  );
}

declare namespace CreatePostButton {
  type Props = Partial<Button.Props>;
}

function CreatePostButton(props: CreatePostButton.Props) {
  return (
    <Link variant="container" href="/create">
      <Button variant="outlined" size="small" {...props}>
         Create
      </Button>
    </Link>
  );
}
